import React, { useState } from "react";
import ConfirmDialog from "./ConfirmDialog";

export const QCDataDev2 = ({
  records,
  editMode,
  editableTitles,
  handleTitleChange,
  handleEditTitle,
  setEditMode,
  cycleMasterId,
  verifyTitle,
  verifyAllTitles,
  statusInfo,
  productsData,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmAction, setConfirmAction] = useState(() => () => {});

  const handleEditClick = (uniqueKey) => {
    setEditMode((prev) => ({ ...prev, [uniqueKey]: true }));
  };

  const formatQcStatus = (status) => {
    if (!status || status === "none") return "Not verified";
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[1080px]">
        {/* Header */}
        <div className="grid grid-cols-[10%_90%]">
          {/* Product Image */}
          <div className="flex justify-center items-center">
            <div className="relative w-48 h-48 rounded-full bg-white border border-gray-300 shadow-sm overflow-hidden">
              <img
                src={
                  productsData[records.productId].product_image_url ||
                  "/feedsumo_2/no_image.png"
                }
                alt={productsData[records.productId].product_name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/feedsumo-new/no_image.png";
                }}
              />
            </div>
          </div>

          {/* Titles Table */}
          <div className="pl-2">
            {/* Table Header */}
            <div className="flex items-center border-b border-gray-300 dark:border-gray-600 pb-2 text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">
              <div className="w-40 text-left">Permutation</div>
              <div className="flex-1 pl-4">Title</div>
              <div className="w-32 text-center">Status</div>
              <div className="w-32 text-center">Actions</div>
            </div>

            {/* Rows */}
            {Array.isArray(records?.title) &&
              records.title.map((item, idx) => {
                const uniqueKey = `${records.productId}_${idx}`;
                const isEditing = editMode[uniqueKey];
                const qcStatus = formatQcStatus(item.qc_status);
                const isVerified = qcStatus === "Verified";

                return (
                  <div
                    key={uniqueKey}
                    className="flex items-center border-b border-gray-100 dark:border-gray-700 py-3 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                  >
                    {/* Permutation */}
                    <div className="w-40 text-gray-800 dark:text-gray-200 font-medium">
                      {item.key === "start"
                        ? "Baseline"
                        : item.key.charAt(0).toUpperCase() + item.key.slice(1)}
                    </div>

                    {/* Title Field */}
                    <div className="flex-1 pl-4 text-gray-800 dark:text-gray-200">
                      {!isEditing ? (
                        <span
                          className="break-words"
                          title={item.qc_status}
                        >
                          {item.title}
                        </span>
                      ) : (
                        <input
                          type="text"
                          value={editableTitles[uniqueKey] || item.title}
                          onChange={(e) => handleTitleChange(e, uniqueKey)}
                          className="border border-gray-300 rounded-md px-3 py-1 w-full focus:ring-2 focus:ring-blue-400 focus:outline-none"
                        />
                      )}
                    </div>

                    {/* Status */}
                    {item.key !== "start" && (
                      <div className="w-32 text-center">
                        <span
                          className={`text-xs px-2 py-1 rounded-full font-semibold ${
                            isVerified
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {qcStatus}
                        </span>
                      </div>
                    )}

                    {/* Actions */}
                    {item.key !== "start" && (
                      <div className="w-32 text-center space-x-2">
                        {!isEditing ? (
                          <>
                            <button
                              disabled={statusInfo.cycle_status === "started"}
                              className={`text-blue-600 hover:underline disabled:text-gray-400`}
                              onClick={() => handleEditClick(uniqueKey)}
                            >
                              Edit
                            </button>

                            <button
                              disabled={statusInfo.cycle_status === "started"}
                              className={`${
                                isVerified
                                  ? "text-red-600"
                                  : "text-green-600"
                              } hover:underline disabled:text-gray-400`}
                              onClick={() =>
                                verifyTitle(
                                  cycleMasterId,
                                  records.productId,
                                  idx,
                                  uniqueKey,
                                  item.key,
                                  isVerified ? "not verified" : "verified"
                                )
                              }
                            >
                              {isVerified ? "Unverify" : "Verify"}
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              disabled={
                                !editableTitles[uniqueKey] ||
                                editableTitles[uniqueKey].trim() ===
                                  item.title.trim()
                              }
                              className="text-green-600 hover:underline disabled:text-gray-400"
                              onClick={() =>
                                handleEditTitle(
                                  cycleMasterId,
                                  records.productId,
                                  idx,
                                  uniqueKey,
                                  editableTitles[uniqueKey],
                                  item.key,
                                  item.title
                                )
                              }
                            >
                              Save
                            </button>
                            <button
                              onClick={() =>
                                setEditMode((prev) => ({
                                  ...prev,
                                  [uniqueKey]: false,
                                }))
                              }
                              className="text-red-500 hover:underline"
                            >
                              Cancel
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

            {/* Verify All Button */}
            <div className="text-right pt-4">
              {records.qc_status === "verified" ? (
                <button
                  disabled={statusInfo.cycle_status === "started"}
                  className="px-4 py-1.5 text-sm bg-red-500 hover:bg-red-600 text-white rounded-md disabled:bg-gray-300 transition"
                  onClick={() =>
                    verifyAllTitles(
                      cycleMasterId,
                      records.productId,
                      "not verified"
                    )
                  }
                >
                  Unverify All
                </button>
              ) : (
                <button
                  disabled={statusInfo.cycle_status === "started"}
                  className="px-4 py-1.5 text-sm bg-green-500 hover:bg-green-600 text-white rounded-md disabled:bg-gray-300 transition"
                  onClick={() =>
                    verifyAllTitles(
                      cycleMasterId,
                      records.productId,
                      "verified"
                    )
                  }
                >
                  Verify All
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          confirmAction();
          setConfirmOpen(false);
        }}
        message={confirmMessage}
      />
    </div>
  );
};
